import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts, fetchPackageProductsDetail } from '../../redux/managePackageSlice';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const Product = ({
  language,
  selectedProductType,
  onUpdate,
  initialSelectedProducts = [],
  initialSelectedDomainType = "",
  packageId
}) => {
  const dispatch = useDispatch();
  const {
    products: apiResponse,
    loading,
    error,
    domainTypeProductMap,
    packageProductsDetailLoading
  } = useSelector(state => state.managePackage);

  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedDomainType, setSelectedDomainType] = useState("");
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  useEffect(() => {
    if (packageId && language) {
      dispatch(fetchPackageProductsDetail({
        packageId,
        region: 'SG'
      }));
    }
  }, [packageId, language, dispatch]);

  useEffect(() => {
    if (language) {
      dispatch(fetchProducts({ language }));
    }
  }, [language, dispatch]);

  useEffect(() => {
    if (apiResponse && apiResponse.resdata && Array.isArray(apiResponse.resdata)) {
      const products = apiResponse.resdata.map(item => {
        const basicInfo = item.contentJson?.basicInfo || {};
        return {
          id: item.uuid,
          uuid: item.uuid,
          name: basicInfo.name,
          description: basicInfo.price,
          domainType: item.domainType || [],
        };
      });

      const sortedProducts = products.sort((a, b) => {
        const nameA = a.name ? a.name.toLowerCase() : '';
        const nameB = b.name ? b.name.toLowerCase() : '';
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
      setAvailableProducts(sortedProducts);
    }
  }, [apiResponse]);

  useEffect(() => {
    if (domainTypeProductMap && selectedProductType) {
      const domainType = selectedProductType.value;
      const productsForDomain = domainTypeProductMap[domainType] || [];

      setSelectedDomainType(domainType);
      setSelectedProducts(productsForDomain);

      setSelectAllChecked(
        productsForDomain.length > 0 &&
        productsForDomain.length === availableProducts.length
      );

      if (onUpdate) {
        onUpdate(productsForDomain, domainType);
      }
    }
  }, [domainTypeProductMap, selectedProductType, onUpdate, availableProducts]);

  const handleSelectAllChange = () => {
    const newSelectAllState = !selectAllChecked;
    setSelectAllChecked(newSelectAllState);

    let newSelectedProducts = [];
    if (newSelectAllState) {
      newSelectedProducts = availableProducts.map(product => product.uuid);
    } else {
      newSelectedProducts = [];
    }

    setSelectedProducts(newSelectedProducts);

    if (onUpdate) {
      onUpdate(newSelectedProducts, selectedDomainType);
    }
  };

  const handleCheckboxChange = (productId) => {
    const newSelectedProducts = selectedProducts.includes(productId)
      ? selectedProducts.filter(id => id !== productId)
      : [...selectedProducts, productId];

    setSelectedProducts(newSelectedProducts);

    const allSelected = availableProducts.length > 0 &&
      newSelectedProducts.length === availableProducts.length;
    setSelectAllChecked(allSelected);

    if (onUpdate) {
      onUpdate(newSelectedProducts, selectedDomainType);
    }
  };

  const getSelectedProductDetails = () => {
    return selectedProducts.map(productId =>
      availableProducts.find(product => product.uuid === productId)
    ).filter(Boolean);
  };

  const getUnselectedProductDetails = () => {
    return availableProducts.filter(product =>
      !selectedProducts.includes(product.uuid)
    );
  };

  const moveSelectedProduct = (fromIndex, toIndex) => {
    const newSelectedProducts = [...selectedProducts];
    const [movedItem] = newSelectedProducts.splice(fromIndex, 1);
    newSelectedProducts.splice(toIndex, 0, movedItem);

    setSelectedProducts(newSelectedProducts);

    if (onUpdate) {
      onUpdate(newSelectedProducts, selectedDomainType);
    }
  };

  if (loading || packageProductsDetailLoading) {
    return (
      <div className="tab-package-container">
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-package-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="tab-package-container">
        <div className="product-selection-container" style={{ display: 'flex', gap: '20px' }}>
          <div className="unselected-products" style={{ flex: 1 }}>
            <h3>Available Products</h3>
            <div className="product-item">
              <label className="product-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectAllChecked}
                  onChange={handleSelectAllChange}
                  className="product-checkbox-input"
                />
                <span className={`product-checkmark ${selectAllChecked ? 'checked' : ''}`}>
                  {selectAllChecked && <span className="checkmark-icon">✓</span>}
                </span>
                <span className="product-text">
                  <span className="product-label">Select All</span>
                </span>
              </label>
            </div>

            <div>
              {getUnselectedProductDetails().map(product => (
                <div key={product.id} className="product-item">
                  <label className="product-checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.uuid)}
                      onChange={() => handleCheckboxChange(product.uuid)}
                      className="product-checkbox-input"
                    />
                    <span className={`product-checkmark ${selectedProducts.includes(product.uuid) ? 'checked' : ''}`}>
                      {selectedProducts.includes(product.uuid) && <span className="checkmark-icon">✓</span>}
                    </span>
                    <span className="product-text">
                      <span className="product-label">{product.name}:</span>
                      {product.description && (
                        <span className="product-name">{product.description}</span>
                      )}
                    </span>
                  </label>
                </div>
              ))}

              {getUnselectedProductDetails().length === 0 && !loading && (
                <div className="no-products">No products available</div>
              )}
            </div>
          </div>

          <div className="selected-products" style={{ flex: 1 }}>
            <h3>Selected Products ({selectedProducts.length})</h3>

            <div>
              {getSelectedProductDetails().map((product, index) => (
                <DraggableProductItem
                  key={product.id}
                  product={product}
                  index={index}
                  moveItem={moveSelectedProduct}
                  handleCheckboxChange={handleCheckboxChange}
                />
              ))}

              {getSelectedProductDetails().length === 0 && (
                <div className="no-products">No products selected</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

const DraggableProductItem = ({ product, index, moveItem, handleCheckboxChange }) => {
  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: 'PRODUCT_ITEM',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveItem(dragIndex, hoverIndex);

      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'PRODUCT_ITEM',
    item: () => {
      return { id: product.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;

  drag(drop(ref));

  return (
    <div
      ref={ref}
      style={{ opacity }}
      className="product-item"
    >
      <label className="product-checkbox-label">
        <input
          type="checkbox"
          checked={true}
          onChange={() => handleCheckboxChange(product.uuid)}
          className="product-checkbox-input"
        />
        <span className={`product-checkmark checked`}>
          <span className="checkmark-icon">✓</span>
        </span>
        <span className="product-text">
          <span className="product-label">{product.name}:</span>
          {product.description && (
            <span className="product-name">{product.description}</span>
          )}
        </span>
      </label>
    </div>
  );
};

export default Product;